import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtIDR, fmtShortIDR, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Receipt, Wallet, CreditCard, QrCode, Banknote, Search, Tag, MessageCircle, Mail, X, Package, RefreshCw, Truck, Plus, Trash2, Percent, Printer, PiggyBank, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useSettings } from "@/contexts/SettingsContext";

const PAYMENT_METHODS = [
  { v: "cash", l: "Cash", icon: Banknote },
  { v: "qris", l: "QRIS", icon: QrCode },
  { v: "card", l: "Debit / Kartu", icon: CreditCard },
  { v: "transfer", l: "Transfer Bank", icon: Wallet },
  { v: "deposit", l: "Potong Deposit", icon: PiggyBank },
  { v: "bonus", l: "Potong Bonus Cashback", icon: PiggyBank },
  { v: "treatment_reward", l: "Potong Reward Treatment", icon: PiggyBank },
];

const INITIATIVES = [
  { v: "regular", l: "Regular" },
  { v: "deposit", l: "Deposit" },
  { v: "referral", l: "Referral" },
  { v: "tebus_murah", l: "Tebus Murah" },
  { v: "lifetime", l: "Lifetime" },
  { v: "exclusive_member", l: "Exclusive Member" },
  { v: "membership", l: "Membership" },
  { v: "birthday", l: "Birthday" },
];

export default function Transactions() {
  const { branchId, branches } = useBranch();
  const { settings } = useSettings();

  // POS form state
  const [patientSearch, setPatientSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [treatmentId, setTreatmentId] = useState("");
  const [treatmentCart, setTreatmentCart] = useState([]);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [activePromos, setActivePromos] = useState([]);
  const [method, setMethod] = useState("qris");
  const [initiative, setInitiative] = useState("regular");
  // Manual discount (independent of promo)
  const [discountType, setDiscountType] = useState("nominal"); // nominal | percent
  const [discountValue, setDiscountValue] = useState("");
  // Split payment
  const [splitMode, setSplitMode] = useState(false);
  const [payments, setPayments] = useState([{ method: "cash", amount: "" }]);
  // Shipping (ekspedisi)
  const [shipEnabled, setShipEnabled] = useState(false);
  const [shipping, setShipping] = useState({ courier: "", tracking_no: "", cost: "", address: "" });
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [therapists, setTherapists] = useState([]);
  const [therapistId, setTherapistId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Retail products (auto-deduct stock)
  const [retailItems, setRetailItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [productPick, setProductPick] = useState("");
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const [recent, setRecent] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  // Item-level promos (harga coret / diskon %) + bundling — synced from "Tambah Data Manual > Promo"
  const [itemPromoMap, setItemPromoMap] = useState({});
  const [bundlePromos, setBundlePromos] = useState([]);
  const [bundlePick, setBundlePick] = useState("");
  const [depositTiers, setDepositTiers] = useState([]);
  const [txMode, setTxMode] = useState("normal"); // normal | topup
  const [topupTierId, setTopupTierId] = useState("");
  const [topupMethod, setTopupMethod] = useState("transfer");
  const [txDeposits, setTxDeposits] = useState([]);
  const [depUses, setDepUses] = useState({}); // { treatment_id: "session"|"nominal" }
  const [depForm, setDepForm] = useState({ treatment_id: "", mode: "session", sessions: "", nominal: "", amount_paid: "", payment_method: "cash" });
  // Paket Member (dijual di POS seperti treatment/produk)
  const [memberPackages, setMemberPackages] = useState([]);
  const [memberPick, setMemberPick] = useState("");
  const [memberSearch, setMemberSearch] = useState("");

  // Branch context for POS - default to first branch user can use
  const txBranchId = branchId === "all" ? branches[0]?.id : branchId;

  // Scope promos strictly to the ACTIVE transaction branch (txBranchId), not the
  // global switcher value (which can be "all"). Prevents cross-branch promo leakage.
  const loadTxDeposits = () => {
    if (patient?.id) api.get(`/patients/${patient.id}/treatment-deposits`).then((r) => setTxDeposits(r.data)).catch(() => setTxDeposits([]));
    else { setTxDeposits([]); setDepUses({}); }
  };
  useEffect(() => { loadTxDeposits(); setDepUses({}); /* eslint-disable-next-line */ }, [patient]);

  const loadActivePromos = () => api.get("/promos", { params: { active_only: true, branch_id: txBranchId } }).then((r) => setActivePromos(r.data));
  useEffect(() => {
    api.get("/treatments").then((r) => setTreatments(r.data));
    api.get("/doctors", { params: { branch_id: branchId } }).then((r) => setDoctors(r.data));
    api.get("/therapists", { params: { branch_id: branchId } }).then((r) => setTherapists(r.data));
    loadActivePromos();
    api.get("/deposit-tiers", { params: { active_only: 1 } }).then((r) => setDepositTiers(r.data)).catch(() => {});
    api.get("/member-packages", { params: { active_only: 1 } }).then((r) => setMemberPackages(r.data)).catch(() => {});
    api.get("/transactions", { params: { branch_id: branchId } }).then((r) => setRecent(r.data.slice(0, 20)));
    api.get("/inventory", { params: { branch_id: branchId, category: "retail" } }).then((r) => setRetailItems(r.data));
    api.get("/item-promos", { params: { active_only: 1, branch_id: txBranchId } }).then((r) => {
      const m = {};
      const bundles = [];
      (r.data || []).forEach((p) => {
        if (p.promo_kind === "bundle") { bundles.push(p); return; }
        const it = (p.items && p.items[0]) || { item_type: p.item_type, item_id: p.item_id, normal_price: p.normal_price };
        m[`${it.item_type}:${it.item_id}`] = {
          name: p.name,
          final_price: p.final_price,
          normal_price: it.normal_price,
          discount_type: p.discount_type,
          discount_pct: p.discount_pct,
          label: p.discount_type === "percent" ? `Disc ${p.discount_pct}%` : "Harga Coret",
        };
      });
      setItemPromoMap(m);
      setBundlePromos(bundles);
    }).catch(() => {});
  }, [branchId, txBranchId]);

  const promoItemName = (type, id) => (type === "treatment"
    ? treatments.find((t) => t.id === id)?.name
    : retailItems.find((r) => r.id === id)?.name) || null;
  const couponItemNames = (p) => [
    ...(p.target_treatment_ids || []).map((id) => promoItemName("treatment", id)),
    ...(p.target_product_ids || []).map((id) => promoItemName("product", id)),
  ].filter(Boolean);
  const singleItemPromos = Object.entries(itemPromoMap).map(([key, v]) => {
    const [type, id] = key.split(":");
    return { key, type, id, itemName: promoItemName(type, id), ...v };
  }).filter((x) => x.itemName);
  const addSinglePromoItem = (sp) => {
    if (sp.type === "treatment") onSelectTreatment(sp.id); else addProduct(sp.id);
  };

  const addBundle = (promoId) => {
    const p = bundlePromos.find((x) => x.id === promoId);
    if (!p) return;
    const items = p.items || [];
    const normalTotal = Number(p.normal_total) || items.reduce((s, i) => s + Number(i.normal_price || 0), 0) || 1;
    const target = Number(p.final_price) || 0;
    const label = p.discount_type === "percent" ? `${p.name} · Disc ${p.discount_pct}%` : p.name;
    let allocated = 0;
    const newT = [...treatmentCart];
    const newP = [...cart];
    items.forEach((it, idx) => {
      let up;
      if (idx === items.length - 1) up = Math.max(0, target - allocated);
      else { up = Math.round(Number(it.normal_price || 0) * target / normalTotal); allocated += up; }
      const line = { lineId: `b-${p.id}-${it.item_id}-${idx}`, name: it.item_name, quantity: 1, unit_price: up, normal_price: Number(it.normal_price || 0), item_promo: true, promo_label: label, bundle_id: p.id };
      if (it.item_type === "treatment") newT.push({ ...line, treatment_id: it.item_id });
      else {
        const inv = retailItems.find((x) => x.id === it.item_id);
        newP.push({ ...line, inventory_id: it.item_id, unit: inv?.unit, stock: inv?.quantity });
      }
    });
    setTreatmentCart(newT);
    setCart(newP);
    setBundlePick("");
    toast.success(`Paket "${p.name}" ditambahkan — ${new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(target)}`);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/patients", { params: { search: patientSearch, limit: 10 } }).then((r) => setPatients(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [patientSearch]);

  // Pilih pasien + auto-tambah rekomendasi dokter (treatment & produk) ke keranjang POS
  const pickPatient = (p) => {
    setPatient(p);
    setPatientSearch("");
    if (txMode === "topup") return;
    const recT = p.recommended_treatment_ids || [];
    const recP = p.recommended_product_ids || [];
    const exT = new Set(treatmentCart.map((c) => c.treatment_id));
    const newT = recT.filter((id) => !exT.has(id)).map((id) => {
      const t = treatments.find((x) => x.id === id);
      if (!t) return null;
      const normal = Number(t.price) || 0;
      const promo = itemPromoMap[`treatment:${id}`];
      const unit = promo ? Number(promo.final_price) : normal;
      return { lineId: `t-${id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, treatment_id: id, name: t.name, quantity: 1, unit_price: unit, normal_price: normal, item_promo: !!promo, promo_label: promo ? promo.label : null, recommended: true };
    }).filter(Boolean);
    const exP = new Set(cart.map((c) => c.inventory_id));
    const newP = recP.filter((id) => !exP.has(id)).map((id) => {
      const it = retailItems.find((x) => x.id === id);
      if (!it) return null;
      const normal = (it.sell_price && it.sell_price > 0) ? it.sell_price : (it.unit_cost || 0);
      const promo = itemPromoMap[`product:${id}`];
      const unit = promo ? Number(promo.final_price) : normal;
      return { lineId: id, inventory_id: id, name: it.name, quantity: 1, unit_price: unit, normal_price: normal, item_promo: !!promo, promo_label: promo ? promo.label : null, unit: it.unit, stock: it.quantity, recommended: true };
    }).filter(Boolean);
    if (newT.length) setTreatmentCart([...treatmentCart, ...newT]);
    if (newP.length) setCart([...cart, ...newP]);
    const added = newT.length + newP.length;
    if (added > 0) toast.success(`${added} rekomendasi dokter otomatis masuk keranjang`);
  };

  const onSelectTreatment = (id) => {
    const t = treatments.find((x) => x.id === id);
    if (!t) return;
    const normal = Number(t.price) || 0;
    const promo = itemPromoMap[`treatment:${id}`];
    const unit = promo ? Number(promo.final_price) : normal;
    setTreatmentCart([...treatmentCart, { lineId: `t-${id}-${Date.now()}`, treatment_id: id, name: t.name, quantity: 1, unit_price: unit, normal_price: normal, item_promo: !!promo, promo_label: promo ? promo.label : null }]);
    setTreatmentId("");
  };
  const updateTreatmentCart = (lineId, patch) => setTreatmentCart(treatmentCart.map((c) => (c.lineId === lineId ? { ...c, ...patch } : c)));
  const removeTreatmentCart = (lineId) => setTreatmentCart(treatmentCart.filter((c) => c.lineId !== lineId));

  const applyPromoObj = (p) => {
    if (!p) return;
    let nextCart = cart.filter((c) => !c.tebus);
    if (p.promo_type === "tebus_murah") {
      const trSum = treatmentCart.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.unit_price || 0), 0);
      const sub = trSum + nextCart.reduce((s, c) => s + Number(c.quantity || 0) * Number(c.unit_price || 0), 0);
      if (sub < Number(p.min_purchase || 0)) {
        toast.error(`Minimal belanja ${fmtIDR(p.min_purchase || 0)} untuk tebus murah ini`);
        return;
      }
      nextCart = [...nextCart, {
        lineId: `tebus-${p.id}`, tebus: true, inventory_id: null,
        name: `Tebus Murah: ${p.redeem_item_name || p.name}`,
        quantity: 1, unit_price: Number(p.redeem_price || 0), promo_code: p.code,
      }];
      setCart(nextCart);
      setAppliedPromo(p);
      toast.success(`Tebus murah "${p.redeem_item_name || p.name}" ditambahkan`);
    } else {
      const tIds = p.target_treatment_ids || [];
      const pIds = p.target_product_ids || [];
      if (tIds.length > 0 || pIds.length > 0) {
        const base = treatmentCart.reduce((s, c) => s + (tIds.includes(c.treatment_id) ? Number(c.quantity || 0) * Number(c.unit_price || 0) : 0), 0)
          + nextCart.reduce((s, c) => s + (pIds.includes(c.inventory_id) ? Number(c.quantity || 0) * Number(c.unit_price || 0) : 0), 0);
        if (base <= 0) {
          toast.error(`Promo "${p.name}" hanya berlaku untuk item tertentu. Tambahkan item yang sesuai ke keranjang.`);
          return;
        }
      }
      setCart(nextCart);
      setAppliedPromo(p);
      toast.success(`Promo ${p.name} diterapkan`);
    }
    setPromoCode(p.code);
  };

  const applyPromo = () => {
    const code = promoCode.toUpperCase().trim();
    const p = activePromos.find((x) => x.code === code);
    if (!p) { toast.error("Kode promo tidak valid / sudah expired"); setAppliedPromo(null); return; }
    applyPromoObj(p);
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setCart((prev) => prev.filter((c) => !c.tebus));
  };

  const addProduct = (id) => {
    const it = retailItems.find((x) => x.id === id);
    if (!it) return;
    if (cart.some((c) => c.inventory_id === id)) { toast.info("Produk sudah ada di keranjang"); return; }
    const normal = (it.sell_price && it.sell_price > 0) ? it.sell_price : (it.unit_cost || 0);
    const promo = itemPromoMap[`product:${id}`];
    const unit = promo ? Number(promo.final_price) : normal;
    setCart([...cart, { lineId: id, inventory_id: id, name: it.name, quantity: 1, unit_price: unit, normal_price: normal, item_promo: !!promo, promo_label: promo ? promo.label : null, unit: it.unit, stock: it.quantity }]);
    setProductPick("");
  };
  const updateCart = (lineId, patch) => setCart(cart.map((c) => (c.lineId === lineId ? { ...c, ...patch } : c)));
  const addMemberPackage = (id) => {
    const p = memberPackages.find((x) => x.id === id);
    if (!p) return;
    if (treatmentCart.some((c) => c.member_package_id)) { toast.info("Hanya 1 Paket Member per transaksi"); setMemberPick(""); return; }
    setTreatmentCart([...treatmentCart, {
      lineId: `mp-${p.id}`, treatment_id: null, member_package_id: p.id, is_member_pkg: true,
      name: `Paket Member - ${p.name}`, quantity: 1, unit_price: Number(p.price) || 0,
      validity_months: p.validity_months,
    }]);
    setMemberPick(""); setMemberSearch("");
  };
  const removeCart = (lineId) => {
    const item = cart.find((c) => c.lineId === lineId);
    if (item?.tebus) { setAppliedPromo(null); setPromoCode(""); }
    setCart(cart.filter((c) => c.lineId !== lineId));
  };

  const lineGross = (c) => Number(c.quantity || 0) * Number(c.unit_price || 0);
  const lineDisc = (c) => { const g = lineGross(c); const v = Number(c.disc_val || 0); const d = c.disc_type === "pct" ? g * v / 100 : v; return Math.min(Math.max(d, 0), g); };
  const lineNet = (c) => lineGross(c) - lineDisc(c);
  const treatmentAmount = treatmentCart.reduce((s, c) => s + lineNet(c), 0);
  const productsTotal = cart.reduce((s, c) => s + lineNet(c), 0);
  const subtotal = treatmentAmount + productsTotal;
  // Promo target — bila promo dibatasi ke item tertentu, diskon hanya dihitung dari item tsb (bundling)
  const promoTargetTreatments = appliedPromo?.target_treatment_ids || [];
  const promoTargetProducts = appliedPromo?.target_product_ids || [];
  const hasPromoTargets = promoTargetTreatments.length > 0 || promoTargetProducts.length > 0;
  const promoBase = (!appliedPromo || !hasPromoTargets)
    ? subtotal
    : treatmentCart.reduce((s, c) => s + (promoTargetTreatments.includes(c.treatment_id) ? lineNet(c) : 0), 0)
      + cart.reduce((s, c) => s + (!c.tebus && promoTargetProducts.includes(c.inventory_id) ? lineNet(c) : 0), 0);
  const promoDiscount = appliedPromo && appliedPromo.promo_type !== "tebus_murah"
    ? (appliedPromo.discount_pct
        ? Math.min(promoBase * appliedPromo.discount_pct / 100, appliedPromo.max_discount || Infinity)
        : Math.min(appliedPromo.discount_amount || 0, promoBase))
    : 0;
  const manualDiscount = discountType === "percent"
    ? subtotal * (Number(discountValue) || 0) / 100
    : (Number(discountValue) || 0);
  const discount = Math.min(promoDiscount + manualDiscount, subtotal);
  const shippingCost = shipEnabled ? (Number(shipping.cost) || 0) : 0;
  const total = Math.max(subtotal - discount, 0) + shippingCost;

  const depFor = (tid) => txDeposits.find((d) => d.treatment_id === tid);
  const depCoveredAmount = treatmentCart.reduce((s, c) => s + (depUses[c.treatment_id] ? lineNet(c) : 0), 0);
  const totalDue = Math.max(total - depCoveredAmount, 0);

  const paymentsSum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const splitRemaining = Math.round((total - paymentsSum) * 100) / 100;
  const addPayment = () => setPayments([...payments, { method: "cash", amount: "" }]);
  const updatePayment = (idx, patch) => setPayments(payments.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const removePayment = (idx) => setPayments(payments.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!patient || !txBranchId || (treatmentCart.length === 0 && cart.length === 0)) { toast.error("Pasien, cabang, & minimal treatment/produk wajib"); return; }
    if (splitMode) {
      if (payments.some((p) => !(Number(p.amount) > 0))) { toast.error("Nominal setiap metode pembayaran wajib diisi"); return; }
      if (Math.abs(splitRemaining) > 1) { toast.error(`Total pembayaran belum pas. Sisa ${fmtIDR(splitRemaining)}`); return; }
    }
    const depositUse = splitMode
      ? payments.filter((p) => p.method === "deposit").reduce((s, p) => s + (Number(p.amount) || 0), 0)
      : (method === "deposit" ? totalDue : 0);
    if (depositUse > (Number(patient.deposit_balance) || 0) + 0.5) {
      toast.error(`Saldo deposit tidak cukup (sisa ${fmtIDR(patient.deposit_balance || 0)}, dibutuhkan ${fmtIDR(depositUse)})`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        patient_id: patient.id,
        branch_id: txBranchId,
        treatment_id: treatmentCart[0]?.treatment_id || null,
        treatment_items: treatmentCart.map((c) => ({ treatment_id: c.treatment_id, name: c.name, quantity: Number(c.quantity), unit_price: Number(c.unit_price), discount_amount: lineDisc(c) })),
        treatment_deposit_uses: Object.keys(depUses).map((tid) => ({ treatment_id: tid, mode: depUses[tid] })),
        member_package_id: treatmentCart.find((c) => c.member_package_id)?.member_package_id || null,
        doctor_id: doctorId || null,
        therapist_id: therapistId || null,
        initiative_type: initiative,
        amount: totalDue,
        subtotal,
        payment_method: splitMode ? "split" : method,
        promo_id: appliedPromo?.id || null,
        promo_code: appliedPromo?.code || null,
        discount_amount: discount || null,
        discount_type: manualDiscount > 0 ? discountType : null,
        discount_value: manualDiscount > 0 ? (Number(discountValue) || 0) : null,
        shipping: shipEnabled
          ? { courier: shipping.courier || null, tracking_no: shipping.tracking_no || null, cost: Number(shipping.cost) || 0, address: shipping.address || null }
          : null,
        payments: splitMode ? payments.map((p) => ({ method: p.method, amount: Number(p.amount) || 0 })) : null,
        product_items: cart.map((c) => ({ inventory_id: c.inventory_id || null, name: c.name, quantity: Number(c.quantity), unit_price: Number(c.unit_price), discount_amount: lineDisc(c) })),
      };
      const { data: tx } = await api.post("/transactions", payload);
      toast.success(`Transaksi ${fmtIDR(totalDue)} ${splitMode ? "(split)" : "via " + method.toUpperCase()} tercatat`);

      // Fetch invoice + open dialog
      try {
        const { data: inv } = await api.get(`/transactions/${tx.id}/invoice`);
        setInvoice(inv);
        if (settings?.auto_send_invoice !== false) setInvoiceOpen(true);
      } catch {}

      // Reset
      setPatient(null); setTreatmentId(""); setTreatmentCart([]); setPromoCode(""); setAppliedPromo(null); setPatientSearch(""); setCart([]); setTherapistId(""); setDoctorId(""); setDepUses({}); setTxDeposits([]);
      setDiscountValue(""); setDiscountType("nominal"); setSplitMode(false); setPayments([{ method: "cash", amount: "" }]); setShipEnabled(false); setShipping({ courier: "", tracking_no: "", cost: "", address: "" });
      api.get("/transactions", { params: { branch_id: branchId } }).then((r) => setRecent(r.data.slice(0, 20)));
      api.get("/inventory", { params: { branch_id: branchId, category: "retail" } }).then((r) => setRetailItems(r.data));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSubmitting(false);
    }
  };

  const topupTier = depositTiers.find((t) => t.id === topupTierId) || null;
  const submitTopup = async (e) => {
    e?.preventDefault?.();
    if (!patient || !txBranchId) { toast.error("Pilih pasien & cabang dulu"); return; }
    if (!topupTier) { toast.error("Pilih tier deposit"); return; }
    setSubmitting(true);
    try {
      const { data: tx } = await api.post("/transactions", {
        patient_id: patient.id,
        branch_id: txBranchId,
        deposit_tier_id: topupTier.id,
        amount: topupTier.deposit_amount,
        payment_method: topupMethod,
        initiative_type: "deposit",
      });
      toast.success(`Top Up ${topupTier.name} berhasil — saldo pasien diperbarui`);
      try {
        const { data: inv } = await api.get(`/transactions/${tx.id}/invoice`);
        setInvoice(inv);
        if (settings?.auto_send_invoice !== false) setInvoiceOpen(true);
      } catch {}
      setPatient(null); setPatientSearch(""); setTopupTierId(""); setTxMode("normal");
      api.get("/transactions", { params: { branch_id: branchId } }).then((r) => setRecent(r.data.slice(0, 20)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal melakukan top up");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDepositTreatment = async (e) => {
    e?.preventDefault?.();
    if (!patient || !txBranchId) { toast.error("Pilih pasien & cabang dulu"); return; }
    if (!depForm.treatment_id) { toast.error("Pilih treatment"); return; }
    if (!(Number(depForm.amount_paid) > 0)) { toast.error("Nominal dibayar harus > 0"); return; }
    if (depForm.mode === "session" && !(Number(depForm.sessions) >= 1)) { toast.error("Jumlah sesi minimal 1"); return; }
    if (depForm.mode === "nominal" && !(Number(depForm.nominal) > 0)) { toast.error("Nominal deposit harus > 0"); return; }
    setSubmitting(true);
    try {
      await api.post("/treatment-deposits", {
        patient_id: patient.id,
        branch_id: txBranchId,
        treatment_id: depForm.treatment_id,
        mode: depForm.mode,
        sessions: depForm.mode === "session" ? Number(depForm.sessions) : 0,
        nominal: depForm.mode === "nominal" ? Number(depForm.nominal) : 0,
        amount_paid: Number(depForm.amount_paid),
        payment_method: depForm.payment_method,
      });
      toast.success("Deposit treatment tersimpan di profil pasien");
      setDepForm({ treatment_id: "", mode: "session", sessions: "", nominal: "", amount_paid: "", payment_method: "cash" });
      loadTxDeposits();
      api.get("/transactions", { params: { branch_id: branchId } }).then((r) => setRecent(r.data.slice(0, 20)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan deposit treatment");
    } finally {
      setSubmitting(false);
    }
  };


  const openWa = () => {
    if (!invoice?.wa_url) { toast.error("Customer tidak memiliki no WA"); return; }
    window.open(invoice.wa_url, "_blank", "noopener,noreferrer");
  };

  const copyInvoiceText = () => {
    if (!invoice?.wa_text) return;
    navigator.clipboard?.writeText(invoice.wa_text);
    toast.success("Teks invoice tersalin");
  };

  const buildReceiptHtml = (inv) => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rp = (n) => "Rp" + Math.round(Number(n) || 0).toLocaleString("id-ID");
    const METHOD_LABEL = { cash: "Tunai", qris: "QRIS", card: "Debit/Kartu", transfer: "Transfer Bank", deposit: "Potong Deposit", bonus: "Bonus Cashback", treatment_reward: "Reward Treatment", split: "Split" };
    const mLabel = (m) => METHOD_LABEL[m] || (m || "-").toUpperCase();

    // Gabung item treatment + produk, hitung gross & diskon per item
    const items = [];
    (inv.treatment_items || []).forEach((t) => items.push({ name: t.name || "Treatment", qty: Number(t.quantity || 1), unit: Number(t.unit_price || 0), disc: Number(t.discount_amount || 0) }));
    (inv.product_items || []).forEach((p) => items.push({ name: p.name || "Produk", qty: Number(p.quantity || 1), unit: Number(p.unit_price || 0), disc: Number(p.discount_amount || 0) }));
    if (items.length === 0 && inv.treatment_name) items.push({ name: inv.treatment_name, qty: 1, unit: Number(inv.subtotal || inv.amount || 0), disc: 0 });

    const itemsGross = items.reduce((s, i) => s + i.qty * i.unit, 0);
    const itemsDisc = items.reduce((s, i) => s + i.disc, 0);
    const subtotalNet = Number(inv.subtotal) > 0 ? Number(inv.subtotal) : (itemsGross - itemsDisc);

    const itemRows = items.map((i) => {
      const gross = i.qty * i.unit;
      let html = `<tr class="it"><td class="nm">${esc(i.name)}<span class="up">${i.qty} x ${rp(i.unit)}</span></td><td class="q">${i.qty}</td><td class="tt">${rp(gross)}</td></tr>`;
      if (i.disc > 0) html += `<tr class="dc"><td class="nm" colspan="2">Diskon ${esc(i.name)}</td><td class="tt">-${rp(i.disc)}</td></tr>`;
      return html;
    }).join("");

    const fmtWaktu = (val) => {
      const d = val ? new Date(val) : new Date();
      if (isNaN(d.getTime())) return String(val || "").slice(0, 16).replace("T", " ");
      return d.toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    };
    const txWhen = fmtWaktu(inv.paid_at);
    const printedWhen = fmtWaktu();

    // Rincian pembayaran — daftar metode + nominal (deposit/bonus/reward sudah termasuk di sini)
    let payRows = "";
    if (inv.payments && inv.payments.length > 1) {
      payRows = inv.payments.map((p) => `<div class="r"><span>${mLabel(p.method)}</span><span>${rp(p.amount)}</span></div>`).join("");
    } else {
      payRows = `<div class="r"><span>${mLabel(inv.payment_method)}</span><span>${rp(inv.amount)}</span></div>`;
    }

    const usedDeposit = Number(inv.deposit_used) > 0 || Number(inv.bonus_used) > 0 || Number(inv.treatment_reward_used) > 0;

    return `<!doctype html><html><head><meta charset="utf-8"><title>Struk ${esc(inv.invoice_no || "")}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: geometricPrecision; }
  body { width: 80mm; padding: 4mm 3.5mm 6mm; font-family: -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; font-size: 11px; line-height: 1.4; color: #000; }
  .center { text-align: center; }
  .clinic { font-size: 15px; font-weight: 800; letter-spacing: .2px; }
  .branch { font-size: 10.5px; font-weight: 700; margin-top: 1px; }
  .sub { font-size: 9px; line-height: 1.35; }
  .inv { font-size: 10px; font-weight: 700; margin-top: 3px; letter-spacing: .3px; }
  .dash { border: none; border-top: 1px dashed #000; margin: 5px 0; }
  .solid { border: none; border-top: 1px solid #000; margin: 4px 0; }
  .meta { font-size: 9.5px; }
  .meta .r { display: flex; justify-content: space-between; gap: 8px; margin: 1px 0; }
  .meta .r span:last-child { text-align: right; font-weight: 600; max-width: 60%; }
  table.items { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .items th { font-size: 8.5px; text-transform: uppercase; letter-spacing: .4px; font-weight: 700; padding: 0 0 3px; border-bottom: 1px solid #000; }
  .items th.h-nm { text-align: left; }
  .items th.h-q { text-align: center; width: 24px; }
  .items th.h-t { text-align: right; width: 66px; }
  .items td { font-size: 10.5px; padding: 3px 0 0; vertical-align: top; word-wrap: break-word; }
  .items td.nm { text-align: left; padding-right: 4px; }
  .items td.q { text-align: center; }
  .items td.tt { text-align: right; white-space: nowrap; }
  .items .up { display: block; font-size: 8.5px; color: #333; font-weight: 400; }
  .items tr.dc td { font-size: 9px; font-style: italic; color: #000; padding-top: 1px; }
  .items tr.dc td.nm { padding-left: 6px; }
  .tot { margin-top: 3px; }
  .tot .r { display: flex; justify-content: space-between; font-size: 10.5px; margin: 1.5px 0; }
  .grand { display: flex; justify-content: space-between; font-size: 13px; font-weight: 800; margin-top: 3px; padding-top: 4px; border-top: 1px solid #000; }
  .pay .r { display: flex; justify-content: space-between; font-size: 10.5px; margin: 1px 0; }
  .pay .r.hdr { font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 2px; }
  .depo { display: flex; justify-content: space-between; font-size: 11px; font-weight: 800; margin-top: 4px; padding: 3px 4px; border: 1px solid #000; }
  .foot { text-align: center; font-size: 9px; margin-top: 8px; line-height: 1.45; }
  .foot.tag { font-weight: 700; margin-top: 3px; }
  img.logo { max-height: 44px; max-width: 55%; display: block; margin: 0 auto 4px; }
</style></head><body onload="window.print(); setTimeout(function(){window.close();}, 300);">
  ${inv.clinic_logo ? `<img class="logo" src="${inv.clinic_logo}" alt="logo"/>` : ""}
  <div class="center clinic">${esc(inv.clinic_name || "MyClinic")}</div>
  ${inv.branch_name ? `<div class="center branch">${esc(inv.branch_name)}</div>` : ""}
  ${inv.clinic_address ? `<div class="center sub">${esc(inv.clinic_address)}</div>` : ""}
  ${inv.clinic_phone ? `<div class="center sub">Telp/WA: ${esc(inv.clinic_phone)}</div>` : ""}
  <div class="center inv">NO. ${esc(inv.invoice_no || "-")}</div>
  <div class="dash"></div>
  <div class="meta">
    <div class="r"><span>Tanggal</span><span>${txWhen} WIB</span></div>
    <div class="r"><span>Pelanggan</span><span>${esc(inv.patient_name || "-")}</span></div>
    ${inv.medical_record_no ? `<div class="r"><span>No. RM</span><span>${esc(inv.medical_record_no)}</span></div>` : ""}
    ${inv.doctor_name ? `<div class="r"><span>Dokter</span><span>${esc(inv.doctor_name)}</span></div>` : ""}
    ${inv.therapist_name ? `<div class="r"><span>Therapist</span><span>${esc(inv.therapist_name)}</span></div>` : ""}
    ${inv.cashier_name ? `<div class="r"><span>Kasir</span><span>${esc(inv.cashier_name)}</span></div>` : ""}
  </div>
  <div class="dash"></div>
  <table class="items">
    <thead><tr><th class="h-nm">Item</th><th class="h-q">Qty</th><th class="h-t">Total</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>
  <div class="solid"></div>
  <div class="tot">
    <div class="r"><span>Subtotal</span><span>${rp(itemsGross)}</span></div>
    ${itemsDisc > 0 ? `<div class="r"><span>Diskon Item</span><span>-${rp(itemsDisc)}</span></div>` : ""}
    ${Number(inv.discount_amount) > 0 ? `<div class="r"><span>Diskon${inv.promo_code ? " (" + esc(inv.promo_code) + ")" : ""}</span><span>-${rp(inv.discount_amount)}</span></div>` : ""}
    ${Number(inv.shipping_cost) > 0 ? `<div class="r"><span>Ongkir${inv.shipping?.courier ? " (" + esc(inv.shipping.courier) + ")" : ""}</span><span>+${rp(inv.shipping_cost)}</span></div>` : ""}
    <div class="grand"><span>TOTAL</span><span>${rp(inv.amount)}</span></div>
  </div>
  <div class="dash"></div>
  <div class="pay">
    <div class="r hdr"><span>Pembayaran</span><span></span></div>
    ${payRows}
  </div>
  ${inv.shipping?.tracking_no ? `<div class="dash"></div><div class="pay"><div class="r"><span>No. Resi</span><span>${esc(inv.shipping.tracking_no)}</span></div></div>` : ""}
  ${usedDeposit ? `<div class="depo"><span>Sisa Saldo (Deposit + Cashback)</span><span>${rp((Number(inv.deposit_balance) || 0) + (Number(inv.bonus_balance) || 0))}</span></div><div class="pay"><div class="r"><span>· Deposit</span><span>${rp(inv.deposit_balance)}</span></div><div class="r"><span>· Cashback</span><span>${rp(inv.bonus_balance)}</span></div></div>` : ""}
  <div class="dash"></div>
  <div class="foot">${esc(inv.invoice_footer || "Terima kasih atas kunjungan Anda.")}</div>
  <div class="foot tag">~ ${esc(inv.clinic_name || "MyClinic")} ~</div>
</body></html>`;
  };

  const printReceipt = (inv) => {
    if (!inv) { toast.error("Data struk tidak tersedia"); return; }
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) { toast.error("Popup diblokir browser. Izinkan popup untuk mencetak struk."); return; }
    w.document.write(buildReceiptHtml(inv));
    w.document.close();
  };

  const printTxById = async (txId) => {
    try {
      const { data: inv } = await api.get(`/transactions/${txId}/invoice`);
      printReceipt(inv);
    } catch {
      toast.error("Gagal memuat struk transaksi");
    }
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight">Transaksi (POS)</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Input transaksi treatment / produk, terapkan promo, pilih metode pembayaran realtime.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Receipt className="h-4 w-4" /> Input Transaksi
            </CardTitle>
            <CardDescription>Cabang: {branches.find((b) => b.id === txBranchId)?.name || "—"}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={txMode === "topup" ? submitTopup : (txMode === "deposit_treatment" ? submitDepositTreatment : submit)} className="space-y-4">
              {/* Mode: Normal vs Top Up Deposit vs Deposit Treatment */}
              <div className="grid grid-cols-3 gap-2" data-testid="tx-mode-toggle">
                <button type="button" data-testid="mode-normal" onClick={() => setTxMode("normal")}
                  className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${txMode === "normal" ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}>
                  Transaksi
                </button>
                <button type="button" data-testid="mode-topup" onClick={() => setTxMode("topup")}
                  className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${txMode === "topup" ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}>
                  Top Up Deposit
                </button>
                <button type="button" data-testid="mode-deposit-treatment" onClick={() => setTxMode("deposit_treatment")}
                  className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${txMode === "deposit_treatment" ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}>
                  Deposit Treatment
                </button>
              </div>

              {/* Patient search */}
              <div>
                <Label>Pasien *</Label>
                {patient ? (
                  <>
                  <div className="flex items-center gap-3 rounded-md border border-border p-2.5">
                    <div className="flex-1">
                      <div className="text-sm font-medium">{patient.name}</div>
                      <div className="text-xs text-muted-foreground">{patient.phone}{patient.address ? ` • ${patient.address}` : ""}</div>
                    </div>
                    <Button type="button" size="sm" variant="ghost" data-testid="clear-patient-btn" onClick={() => setPatient(null)}>Ganti</Button>
                  </div>
                  {(Number(patient.deposit_balance) > 0 || Number(patient.bonus_balance) > 0 || Number(patient.treatment_reward_balance) > 0) && (
                    <div className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 space-y-0.5" data-testid="deposit-alert">
                      <div className="flex items-center gap-2 font-medium"><Wallet className="h-4 w-4" /> Saldo Member</div>
                      <div className="flex justify-between"><span>Deposit</span><b data-testid="alert-deposit">{fmtIDR(patient.deposit_balance || 0)}</b></div>
                      {Number(patient.bonus_balance) > 0 && <div className="flex justify-between"><span>Bonus Cashback</span><b data-testid="alert-bonus">{fmtIDR(patient.bonus_balance)}</b></div>}
                      {Number(patient.treatment_reward_balance) > 0 && <div className="flex justify-between text-amber-700"><span>Reward Treatment</span><b data-testid="alert-reward">{fmtIDR(patient.treatment_reward_balance)}</b></div>}
                    </div>
                  )}
                  {txDeposits.length > 0 && (
                    <div className="mt-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 space-y-0.5" data-testid="treatment-deposit-alert">
                      <div className="flex items-center gap-2 font-medium"><Wallet className="h-4 w-4" /> Saldo Deposit Treatment</div>
                      {txDeposits.map((d) => (
                        <div key={d.treatment_id} className="flex justify-between" data-testid={`tdep-${d.treatment_id}`}>
                          <span>{d.treatment_name}</span>
                          <b>
                            {Number(d.sessions_remaining) > 0 ? `${d.sessions_remaining} sesi` : ""}
                            {Number(d.sessions_remaining) > 0 && Number(d.nominal_remaining) > 0 ? " + " : ""}
                            {Number(d.nominal_remaining) > 0 ? fmtIDR(d.nominal_remaining) : ""}
                          </b>
                        </div>
                      ))}
                    </div>
                  )}
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input data-testid="tx-patient-search" placeholder="Cari nama / no HP / alamat / No. RM..." value={patientSearch} onChange={(e) => setPatientSearch(e.target.value)} className="pl-8" />
                    </div>
                    {patientSearch && patients.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-border">
                        {patients.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            data-testid={`pick-${p.id}`}
                            onClick={() => pickPatient(p)}
                            className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                          >
                            <div className="font-medium">{p.name}{Number(p.deposit_balance) > 0 ? <span className="ml-2 text-xs text-emerald-700">💰 {fmtIDR(p.deposit_balance)}</span> : null}</div>
                            <div className="text-xs text-muted-foreground">{p.phone}{p.address ? ` • ${p.address}` : ""}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {txMode === "topup" && (
                <div className="space-y-4" data-testid="topup-panel">
                  <div>
                    <Label>Pilih Tier Deposit *</Label>
                    <Select value={topupTierId} onValueChange={setTopupTierId}>
                      <SelectTrigger data-testid="topup-tier-select" className="mt-1"><SelectValue placeholder="Pilih tier deposit..." /></SelectTrigger>
                      <SelectContent>
                        {depositTiers.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`topup-tier-opt-${t.id}`}>
                            {t.name} — Bayar {fmtIDR(t.deposit_amount)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {depositTiers.length === 0 && <p className="mt-1 text-xs text-amber-600">Belum ada skema tier. Atur di Super Admin → Skema Deposit.</p>}
                  </div>

                  {topupTier && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 space-y-1.5 text-sm" data-testid="topup-breakdown">
                      <div className="flex justify-between"><span className="text-muted-foreground">Dibayar pasien</span><b>{fmtIDR(topupTier.deposit_amount)}</b></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Saldo deposit masuk</span><span>{fmtIDR(topupTier.deposit_amount)}</span></div>
                      <div className="flex justify-between text-emerald-700"><span>Bonus cashback</span><span>+{fmtIDR(topupTier.cashback_amount)}</span></div>
                      <div className="flex justify-between text-amber-700"><span>Reward free treatment</span><span>+{fmtIDR(topupTier.free_treatment_reward)}</span></div>
                      <div className="flex justify-between font-heading text-base pt-1.5 border-t border-emerald-200"><span>Total nilai diterima</span><b data-testid="topup-total-value">{fmtIDR((topupTier.deposit_amount || 0) + (topupTier.cashback_amount || 0) + (topupTier.free_treatment_reward || 0))}</b></div>
                    </div>
                  )}

                  <div>
                    <Label>Metode Pembayaran Top Up</Label>
                    <Select value={topupMethod} onValueChange={setTopupMethod}>
                      <SelectTrigger data-testid="topup-method-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="qris">QRIS</SelectItem>
                        <SelectItem value="card">Debit / Kartu</SelectItem>
                        <SelectItem value="transfer">Transfer Bank</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button type="submit" className="w-full" data-testid="topup-submit-btn" disabled={submitting || !topupTier || !patient}>
                    {submitting ? "Memproses..." : topupTier ? `Catat Top Up ${fmtIDR(topupTier.deposit_amount)}` : "Catat Top Up"}
                  </Button>
                </div>
              )}

              {txMode === "deposit_treatment" && (
                <div className="space-y-4" data-testid="deposit-treatment-panel">
                  <div>
                    <Label>Treatment yang di-deposit *</Label>
                    <Select value={depForm.treatment_id} onValueChange={(v) => setDepForm({ ...depForm, treatment_id: v })}>
                      <SelectTrigger data-testid="dep-treatment-select" className="mt-1"><SelectValue placeholder="Pilih treatment..." /></SelectTrigger>
                      <SelectContent>
                        {treatments.map((t) => (<SelectItem key={t.id} value={t.id} data-testid={`dep-treatment-opt-${t.id}`}>{t.name} — {fmtIDR(t.price)}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Jenis Deposit</Label>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <button type="button" data-testid="dep-mode-session" onClick={() => setDepForm({ ...depForm, mode: "session" })}
                        className={`rounded-md border px-3 py-2 text-sm font-medium ${depForm.mode === "session" ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}>Jumlah Sesi</button>
                      <button type="button" data-testid="dep-mode-nominal" onClick={() => setDepForm({ ...depForm, mode: "nominal" })}
                        className={`rounded-md border px-3 py-2 text-sm font-medium ${depForm.mode === "nominal" ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"}`}>Nominal Rupiah</button>
                    </div>
                  </div>
                  {depForm.mode === "session" ? (
                    <div>
                      <Label>Jumlah Sesi dibeli *</Label>
                      <Input type="number" min="1" data-testid="dep-sessions" className="mt-1" value={depForm.sessions} onChange={(e) => setDepForm({ ...depForm, sessions: e.target.value })} placeholder="mis. 5" />
                    </div>
                  ) : (
                    <div>
                      <Label>Nominal Deposit (Rp) *</Label>
                      <Input type="number" min="0" data-testid="dep-nominal" className="mt-1" value={depForm.nominal} onChange={(e) => setDepForm({ ...depForm, nominal: e.target.value })} placeholder="mis. 2000000" />
                    </div>
                  )}
                  <div>
                    <Label>Nominal Dibayar Pasien (Rp) *</Label>
                    <Input type="number" min="0" data-testid="dep-amount-paid" className="mt-1" value={depForm.amount_paid} onChange={(e) => setDepForm({ ...depForm, amount_paid: e.target.value })} placeholder="mis. 1750000" />
                  </div>
                  <div>
                    <Label>Metode Pembayaran</Label>
                    <Select value={depForm.payment_method} onValueChange={(v) => setDepForm({ ...depForm, payment_method: v })}>
                      <SelectTrigger data-testid="dep-method-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="qris">QRIS</SelectItem>
                        <SelectItem value="card">Debit / Kartu</SelectItem>
                        <SelectItem value="transfer">Transfer Bank</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" data-testid="dep-submit-btn" disabled={submitting || !patient || !depForm.treatment_id}>
                    {submitting ? "Memproses..." : "Simpan Deposit Treatment"}
                  </Button>
                </div>
              )}

              {txMode === "normal" && (<div className="space-y-4">

              {bundlePromos.length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                  <Label className="text-emerald-800">Paket Promo (Bundling)</Label>
                  <Select value={bundlePick} onValueChange={addBundle}>
                    <SelectTrigger data-testid="tx-bundle-select" className="mt-1 bg-white"><SelectValue placeholder="Pilih paket bundling..." /></SelectTrigger>
                    <SelectContent>
                      {bundlePromos.map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`tx-bundle-opt-${p.id}`}>
                          {p.name} — <span className="line-through text-muted-foreground mr-1">{fmtIDR(p.normal_total)}</span>
                          <span className="text-emerald-600 font-medium">{fmtIDR(p.final_price)}</span>
                          {p.discount_type === "percent" ? ` (Disc ${p.discount_pct}%)` : ""} · {p.items?.length || 0} item
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-emerald-700">Memilih paket akan menambahkan semua item paket ke keranjang dengan harga promo.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Treatment (bisa lebih dari satu)</Label>
                  <Select value={treatmentId} onValueChange={onSelectTreatment}>
                    <SelectTrigger data-testid="tx-treatment-select"><SelectValue placeholder="Tambah treatment..." /></SelectTrigger>
                    <SelectContent>
                      <div className="sticky top-0 z-10 bg-popover p-1.5">
                        <Input
                          data-testid="tx-treatment-search"
                          placeholder="Cari treatment..."
                          value={treatmentSearch}
                          onChange={(e) => setTreatmentSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="h-8"
                        />
                      </div>
                      {treatments.filter((t) => t.name.toLowerCase().includes(treatmentSearch.toLowerCase())).map((t) => {
                        const tp = itemPromoMap[`treatment:${t.id}`];
                        return (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} — {tp
                              ? <span><span className="line-through text-muted-foreground mr-1">{fmtIDR(t.price)}</span><span className="text-emerald-600 font-medium">{fmtIDR(tp.final_price)}</span> <span className="text-[10px] text-emerald-600">{tp.discount_type === "percent" ? `DISC ${tp.discount_pct}%` : "PROMO"}</span></span>
                              : fmtIDR(t.price)}
                          </SelectItem>
                        );
                      })}
                      {treatments.filter((t) => t.name.toLowerCase().includes(treatmentSearch.toLowerCase())).length === 0 && (
                        <div className="px-2 py-1.5 text-xs text-muted-foreground">Tidak ada treatment cocok</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Dokter</Label>
                  <Select value={doctorId || "none"} onValueChange={(v) => setDoctorId(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="tx-doctor-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Tidak ditentukan —</SelectItem>
                      {doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Therapist (tagging)</Label>
                <Select value={therapistId || "none"} onValueChange={(v) => setTherapistId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="tx-therapist-select"><SelectValue placeholder="Pilih therapist" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Tidak ditentukan —</SelectItem>
                    {therapists.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {treatmentCart.length > 0 && (
                <div className="space-y-2" data-testid="tx-treatment-cart">
                  {treatmentCart.map((c) => (
                    <div key={c.lineId} className="flex items-center gap-2 rounded-md border border-border p-2" data-testid={`tcart-${c.lineId}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.is_member_pkg ? `Paket Member${c.validity_months ? ` · berlaku ${c.validity_months} bln` : ""}` : "Treatment"}
                          {c.recommended && <span className="ml-1.5 rounded bg-indigo-100 px-1 py-0.5 text-[10px] font-medium text-indigo-700" data-testid={`tcart-reco-${c.lineId}`}>Rekomendasi Dokter</span>}
                          {c.item_promo && (
                            <span className="ml-1.5"><span className="line-through">{fmtIDR(c.normal_price)}</span> <span className="text-emerald-600 font-medium">{c.promo_label || "PROMO"}</span></span>
                          )}
                        </div>
                      </div>
                      <Input type="number" min="1" step="1" className="w-14" data-testid={`tcart-qty-${c.lineId}`} value={c.quantity} onChange={(e) => updateTreatmentCart(c.lineId, { quantity: e.target.value })} title="Qty" />
                      <Input type="number" min="0" step="1" className="w-24" data-testid={`tcart-price-${c.lineId}`} value={c.unit_price} onChange={(e) => updateTreatmentCart(c.lineId, { unit_price: e.target.value })} title="Harga satuan" />
                      <Input type="number" min="0" step="1" className="w-16" data-testid={`tcart-disc-${c.lineId}`} value={c.disc_val || ""} placeholder="Disc" onChange={(e) => updateTreatmentCart(c.lineId, { disc_val: e.target.value })} title="Diskon per item" />
                      <button type="button" data-testid={`tcart-disctype-${c.lineId}`} onClick={() => updateTreatmentCart(c.lineId, { disc_type: c.disc_type === "pct" ? "rp" : "pct" })} className="w-9 shrink-0 rounded-md border text-xs py-2 hover:bg-secondary" title="Tipe diskon">{c.disc_type === "pct" ? "%" : "Rp"}</button>
                      <span className="w-24 text-right text-sm">
                        {lineDisc(c) > 0 && <span className="block text-[10px] text-muted-foreground line-through">{fmtIDR(lineGross(c))}</span>}
                        <span className={lineDisc(c) > 0 ? "text-emerald-700 font-medium" : ""}>{fmtIDR(lineNet(c))}</span>
                      </span>
                      <Button type="button" size="sm" variant="ghost" data-testid={`tcart-remove-${c.lineId}`} onClick={() => removeTreatmentCart(c.lineId)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {treatmentCart.map((c) => {
                    const d = depFor(c.treatment_id);
                    if (!d) return null;
                    const checked = !!depUses[c.treatment_id];
                    const canSession = Number(d.sessions_remaining) > 0;
                    const canNominal = Number(d.nominal_remaining) + 0.5 >= lineNet(c);
                    if (!canSession && !canNominal) return null;
                    return (
                      <label key={`use-${c.lineId}`} data-testid={`use-deposit-${c.treatment_id}`} className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs text-sky-800 cursor-pointer">
                        <input type="checkbox" data-testid={`use-deposit-cb-${c.treatment_id}`} checked={checked}
                          onChange={(e) => {
                            const next = { ...depUses };
                            if (e.target.checked) { next[c.treatment_id] = canSession ? "session" : "nominal"; setSplitMode(false); }
                            else delete next[c.treatment_id];
                            setDepUses(next);
                          }} />
                        Gunakan Deposit Treatment untuk <b>{c.name}</b>
                        <span className="ml-auto">{canSession ? `${d.sessions_remaining} sesi` : fmtIDR(d.nominal_remaining)}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Retail products */}
              <div>
                <Label className="flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Produk Retail (stok terpotong otomatis)</Label>
                <Select value={productPick} onValueChange={addProduct}>
                  <SelectTrigger data-testid="tx-product-select"><SelectValue placeholder="Tambah produk retail..." /></SelectTrigger>
                  <SelectContent>
                    <div className="sticky top-0 z-10 bg-popover p-1.5">
                      <Input
                        data-testid="tx-product-search"
                        placeholder="Cari produk..."
                        value={productSearch}
                        onChange={(e) => setProductSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-8"
                      />
                    </div>
                    {retailItems.filter((i) => !cart.some((c) => c.inventory_id === i.id) && i.name.toLowerCase().includes(productSearch.toLowerCase())).map((i) => {
                      const pp = itemPromoMap[`product:${i.id}`];
                      const base = i.sell_price || i.unit_cost || 0;
                      return (
                        <SelectItem key={i.id} value={i.id} disabled={i.quantity <= 0}>
                          {i.name} — {pp
                            ? <span><span className="line-through text-muted-foreground mr-1">{fmtIDR(base)}</span><span className="text-emerald-600 font-medium">{fmtIDR(pp.final_price)}</span> <span className="text-[10px] text-emerald-600">{pp.discount_type === "percent" ? `DISC ${pp.discount_pct}%` : "PROMO"}</span></span>
                            : fmtIDR(base)} · stok {i.quantity} {i.unit}{i.quantity <= 0 ? " (habis)" : ""}
                        </SelectItem>
                      );
                    })}
                    {retailItems.filter((i) => !cart.some((c) => c.inventory_id === i.id) && i.name.toLowerCase().includes(productSearch.toLowerCase())).length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Tidak ada produk cocok</div>
                    )}
                  </SelectContent>
                </Select>
                {cart.length > 0 && (
                  <div className="mt-2 space-y-2" data-testid="tx-cart">
                    {cart.map((c) => (
                      <div key={c.lineId} className={`flex items-center gap-2 rounded-md border p-2 ${c.tebus ? "border-emerald-300 bg-emerald-50/50" : "border-border"}`} data-testid={`cart-${c.lineId}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {c.tebus ? "Harga tebus (promo)" : `stok ${c.stock} ${c.unit}`}
                            {c.recommended && !c.tebus && <span className="ml-1.5 rounded bg-indigo-100 px-1 py-0.5 text-[10px] font-medium text-indigo-700" data-testid={`cart-reco-${c.lineId}`}>Rekomendasi Dokter</span>}
                            {c.item_promo && !c.tebus && (
                              <span className="ml-1.5"><span className="line-through">{fmtIDR(c.normal_price)}</span> <span className="text-emerald-600 font-medium">{c.promo_label || "PROMO"}</span></span>
                            )}
                          </div>
                        </div>
                        <Input type="number" min="1" step="1" className="w-14" data-testid={`cart-qty-${c.lineId}`} value={c.quantity} onChange={(e) => updateCart(c.lineId, { quantity: e.target.value })} title="Qty" />
                        <Input type="number" min="0" step="1" className="w-24" data-testid={`cart-price-${c.lineId}`} value={c.unit_price} disabled={c.tebus} onChange={(e) => updateCart(c.lineId, { unit_price: e.target.value })} title="Harga satuan" />
                        <Input type="number" min="0" step="1" className="w-16" data-testid={`cart-disc-${c.lineId}`} value={c.disc_val || ""} placeholder="Disc" disabled={c.tebus} onChange={(e) => updateCart(c.lineId, { disc_val: e.target.value })} title="Diskon per item" />
                        <button type="button" data-testid={`cart-disctype-${c.lineId}`} disabled={c.tebus} onClick={() => updateCart(c.lineId, { disc_type: c.disc_type === "pct" ? "rp" : "pct" })} className="w-9 shrink-0 rounded-md border text-xs py-2 hover:bg-secondary disabled:opacity-40" title="Tipe diskon">{c.disc_type === "pct" ? "%" : "Rp"}</button>
                        <span className="w-24 text-right text-sm">
                          {lineDisc(c) > 0 && <span className="block text-[10px] text-muted-foreground line-through">{fmtIDR(lineGross(c))}</span>}
                          <span className={lineDisc(c) > 0 ? "text-emerald-700 font-medium" : ""}>{fmtIDR(lineNet(c))}</span>
                        </span>
                        <Button type="button" size="sm" variant="ghost" data-testid={`cart-remove-${c.lineId}`} onClick={() => removeCart(c.lineId)}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Paket Member — dijual seperti treatment/produk; set status member pasien */}
              <div>
                <Label className="flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Paket Member</Label>
                <Select value={memberPick} onValueChange={addMemberPackage}>
                  <SelectTrigger data-testid="tx-member-select"><SelectValue placeholder="Tambah paket member..." /></SelectTrigger>
                  <SelectContent>
                    <div className="sticky top-0 z-10 bg-popover p-1.5">
                      <Input
                        data-testid="tx-member-search"
                        placeholder="Cari paket member..."
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-8"
                      />
                    </div>
                    {memberPackages.filter((p) => p.name.toLowerCase().includes(memberSearch.toLowerCase())).map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`tx-member-opt-${p.id}`}>
                        {p.name} — {fmtIDR(p.price)} · berlaku {p.validity_months} bln
                      </SelectItem>
                    ))}
                    {memberPackages.length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Belum ada paket member. Atur di menu Migrasi Data → Katalog Paket Member.</div>
                    )}
                    {memberPackages.length > 0 && memberPackages.filter((p) => p.name.toLowerCase().includes(memberSearch.toLowerCase())).length === 0 && (
                      <div className="px-2 py-1.5 text-xs text-muted-foreground">Tidak ada paket cocok</div>
                    )}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-muted-foreground">Memilih paket akan menambah item ke keranjang & otomatis mengaktifkan status member pasien saat transaksi disimpan.</p>
              </div>

              <div>
                <Label>Tipe Inisiatif</Label>
                <Select value={initiative} onValueChange={setInitiative}>
                  <SelectTrigger data-testid="tx-init-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INITIATIVES.map((i) => <SelectItem key={i.v} value={i.v}>{i.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Promo */}
              <div>
                <Label>Kode Promo</Label>
                <div className="flex gap-2">
                  <Input data-testid="tx-promo-input" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="WELCOME20" />
                  {appliedPromo ? (
                    <Button type="button" variant="outline" data-testid="remove-promo-btn" onClick={removePromo}>Hapus</Button>
                  ) : (
                    <Button type="button" variant="outline" data-testid="apply-promo-btn" onClick={applyPromo}>Apply</Button>
                  )}
                </div>
                {appliedPromo && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs text-emerald-800" data-testid="applied-promo">
                    <Tag className="h-3 w-3" /> {appliedPromo.name}{appliedPromo.promo_type === "tebus_murah"
                      ? ` — tebus ${fmtIDR(appliedPromo.redeem_price || 0)}`
                      : ` — diskon ${appliedPromo.discount_pct ? `${appliedPromo.discount_pct}%` : fmtIDR(appliedPromo.discount_amount)}`}
                  </div>
                )}
              </div>

              {/* Manual Discount */}
              <div>
                <Label className="flex items-center gap-1.5"><Percent className="h-3.5 w-3.5" /> Diskon Manual</Label>
                <div className="mt-1 flex gap-2">
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger className="w-40" data-testid="tx-discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nominal">Nominal (Rp)</SelectItem>
                      <SelectItem value="percent">Persentase (%)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" min="0" step="1"
                    data-testid="tx-discount-value"
                    placeholder={discountType === "percent" ? "cth. 10" : "cth. 50000"}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                  />
                </div>
                {manualDiscount > 0 && (
                  <p className="mt-1 text-xs text-emerald-700" data-testid="tx-manual-discount-info">
                    Potongan manual: -{fmtIDR(Math.min(manualDiscount, subtotal))}
                  </p>
                )}
              </div>

              {/* Shipping / Ekspedisi */}
              <div className="rounded-md border border-border p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="tx-shipping-toggle" checked={shipEnabled} onChange={(e) => setShipEnabled(e.target.checked)} />
                  <span className="text-sm font-medium flex items-center gap-1.5"><Truck className="h-4 w-4" /> Pengiriman Produk (Ekspedisi)</span>
                </label>
                {shipEnabled && (
                  <div className="mt-3 grid grid-cols-2 gap-3" data-testid="tx-shipping-fields">
                    <div>
                      <Label>Nama Ekspedisi</Label>
                      <Input data-testid="tx-ship-courier" placeholder="JNE / J&T / SiCepat" value={shipping.courier} onChange={(e) => setShipping({ ...shipping, courier: e.target.value })} />
                    </div>
                    <div>
                      <Label>Nomor Resi</Label>
                      <Input data-testid="tx-ship-resi" placeholder="cth. JP1234567890" value={shipping.tracking_no} onChange={(e) => setShipping({ ...shipping, tracking_no: e.target.value })} />
                    </div>
                    <div>
                      <Label>Biaya Ongkir (Rp)</Label>
                      <Input type="number" min="0" step="1" data-testid="tx-ship-cost" value={shipping.cost} onChange={(e) => setShipping({ ...shipping, cost: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label>Alamat Pengiriman</Label>
                      <Input data-testid="tx-ship-address" placeholder="Alamat lengkap penerima" value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} />
                    </div>
                  </div>
                )}
              </div>

              {/* Payment method */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Metode Pembayaran</Label>
                  <label className="flex items-center gap-1.5 text-xs cursor-pointer text-muted-foreground">
                    <input type="checkbox" data-testid="tx-split-toggle" checked={splitMode} onChange={(e) => setSplitMode(e.target.checked)} />
                    Split Payment (>1 metode)
                  </label>
                </div>
                {!splitMode ? (
                  <div className="mt-1 grid grid-cols-4 gap-2">
                    {PAYMENT_METHODS.map((m) => {
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.v}
                          type="button"
                          data-testid={`method-${m.v}`}
                          onClick={() => setMethod(m.v)}
                          className={`rounded-md border px-3 py-3 text-sm flex flex-col items-center gap-1 transition-colors ${
                            method === m.v ? "border-foreground bg-foreground text-background" : "border-border hover:bg-secondary"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {m.l}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-2 space-y-2" data-testid="tx-split-list">
                    {payments.map((p, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`split-row-${idx}`}>
                        <Select value={p.method} onValueChange={(v) => updatePayment(idx, { method: v })}>
                          <SelectTrigger className="w-40" data-testid={`split-method-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" min="0" step="1" className="flex-1" data-testid={`split-amount-${idx}`} placeholder="Nominal" value={p.amount} onChange={(e) => updatePayment(idx, { amount: e.target.value })} />
                        <Button type="button" size="sm" variant="ghost" data-testid={`split-remove-${idx}`} onClick={() => removePayment(idx)} disabled={payments.length <= 1}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <Button type="button" size="sm" variant="outline" data-testid="split-add-btn" onClick={addPayment}><Plus className="mr-1 h-3.5 w-3.5" /> Tambah Metode</Button>
                      <span className={`text-xs ${Math.abs(splitRemaining) <= 1 ? "text-emerald-700" : "text-rose-600"}`} data-testid="split-remaining">
                        {Math.abs(splitRemaining) <= 1 ? "Pas ✓" : `Sisa ${fmtIDR(splitRemaining)}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-secondary/50 p-4 space-y-1.5 text-sm">
                {treatmentAmount > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Treatment ({treatmentCart.length})</span><span>{fmtIDR(treatmentAmount)}</span></div>}
                {productsTotal > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Produk Retail ({cart.length})</span><span>{fmtIDR(productsTotal)}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{fmtIDR(subtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-700"><span>Diskon</span><span>-{fmtIDR(discount)}</span></div>}
                {shippingCost > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Ongkir{shipping.courier ? ` (${shipping.courier})` : ""}</span><span data-testid="tx-shipping-line">+{fmtIDR(shippingCost)}</span></div>}
                <div className="flex justify-between font-heading text-lg pt-1 border-t border-border"><span>Total</span><span data-testid="tx-total">{fmtIDR(total)}</span></div>
                {depCoveredAmount > 0 && (
                  <>
                    <div className="flex justify-between text-sky-700"><span>Deposit Treatment</span><span data-testid="tx-deposit-covered">-{fmtIDR(depCoveredAmount)}</span></div>
                    <div className="flex justify-between font-heading text-lg"><span>Sisa Dibayar</span><span data-testid="tx-total-due">{fmtIDR(totalDue)}</span></div>
                  </>
                )}
              </div>

              <Button type="submit" className="w-full" data-testid="tx-submit-btn" disabled={submitting}>
                {submitting ? "Memproses..." : `Catat Transaksi ${fmtIDR(totalDue)}`}
              </Button>
              </div>)}
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="font-heading text-lg">Promo Berlaku</CardTitle>
              <CardDescription>{activePromos.length + bundlePromos.length + singleItemPromos.length} promo aktif</CardDescription>
            </div>
            <Button type="button" size="sm" variant="ghost" data-testid="refresh-promos-btn" onClick={loadActivePromos} title="Muat ulang promo">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Single-item promos (harga coret / disc per item) */}
            {singleItemPromos.map((sp) => (
              <button
                key={`single-${sp.key}`}
                type="button"
                data-testid={`use-item-promo-${sp.id}`}
                onClick={() => addSinglePromoItem(sp)}
                className="block w-full text-left rounded-md border border-amber-200 bg-amber-50/40 p-3 hover:bg-amber-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{sp.name}</span>
                  <Badge className="bg-amber-500 text-white hover:bg-amber-500 text-[10px]">{sp.discount_type === "percent" ? `Disc ${sp.discount_pct}%` : "Harga Coret"}</Badge>
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="text-[9px] rounded bg-secondary px-1 py-0.5">{sp.type === "treatment" ? "Treatment" : "Produk"}</span>
                  <span className="truncate text-foreground">{sp.itemName}</span>
                </div>
                <div className="mt-1.5 text-sm">
                  <span className="text-muted-foreground line-through mr-1.5">{fmtIDR(sp.normal_price)}</span>
                  <span className="font-semibold text-amber-700">{fmtIDR(sp.final_price)}</span>
                </div>
              </button>
            ))}

            {/* Bundle / item packages (harga coret) */}
            {bundlePromos.map((p) => {
              const items = p.items || [];
              return (
                <button
                  key={`bundle-${p.id}`}
                  type="button"
                  data-testid={`use-bundle-${p.id}`}
                  onClick={() => addBundle(p.id)}
                  className="block w-full text-left rounded-md border border-emerald-200 bg-emerald-50/40 p-3 hover:bg-emerald-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{p.name}</span>
                    <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px]">
                      {p.discount_type === "percent" ? `Disc ${p.discount_pct}%` : "Harga Coret"}
                    </Badge>
                  </div>
                  <ul className="mt-1.5 space-y-0.5" data-testid={`bundle-items-${p.id}`}>
                    {items.map((it, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <span className="text-emerald-600">•</span>
                        <span className="text-[9px] rounded bg-secondary px-1 py-0.5">{it.item_type === "treatment" ? "Treatment" : "Produk"}</span>
                        <span className="truncate">{it.item_name}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1.5 text-sm">
                    <span className="text-muted-foreground line-through mr-1.5">{fmtIDR(p.normal_total)}</span>
                    <span className="font-semibold text-emerald-700" data-testid={`bundle-final-${p.id}`}>{fmtIDR(p.final_price)}</span>
                    {items.length > 1 && <span className="ml-1.5 text-[10px] text-muted-foreground">· paket {items.length} item</span>}
                  </div>
                </button>
              );
            })}

            {/* Coupon codes */}
            {activePromos.map((p) => {
              const names = couponItemNames(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`use-promo-${p.id}`}
                  onClick={() => applyPromoObj(p)}
                  className="block w-full text-left rounded-md border border-border p-3 hover:bg-secondary transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <code className="text-xs font-bold tracking-wider rounded bg-foreground text-background px-2 py-0.5">{p.code}</code>
                    {p.promo_type === "tebus_murah"
                      ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Tebus Murah</Badge>
                      : <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100 text-[10px]" data-testid={`promo-disc-${p.id}`}>{p.discount_pct ? `Diskon ${p.discount_pct}%` : p.discount_amount ? `Potongan ${fmtIDR(p.discount_amount)}` : "Diskon"}</Badge>}
                  </div>
                  <div className="mt-1.5 text-sm font-medium">{p.name}</div>
                  {p.description && <div className="text-xs text-muted-foreground">{p.description}</div>}
                  {p.promo_type === "tebus_murah" ? (
                    <div className="mt-1 text-xs text-emerald-700">Tebus {p.redeem_item_name || ""} {fmtIDR(p.redeem_price || 0)} <span className="text-muted-foreground">(min. belanja {fmtIDR(p.min_purchase || 0)})</span></div>
                  ) : names.length > 0 ? (
                    <div className="mt-1.5" data-testid={`promo-items-${p.id}`}>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{names.length > 1 ? `Paket ${names.length} item:` : "Berlaku untuk:"}</div>
                      <ul className="mt-0.5 space-y-0.5">
                        {names.map((n, i) => (
                          <li key={i} className="text-xs text-foreground flex items-center gap-1.5"><span className="text-sky-600">•</span><span className="truncate">{n}</span></li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-muted-foreground">Berlaku untuk semua item</div>
                  )}
                  <div className="mt-1 text-[10px] text-muted-foreground">Berlaku s/d {p.end_at?.slice(0, 10)}</div>
                </button>
              );
            })}
            {activePromos.length + bundlePromos.length + singleItemPromos.length === 0 && <div className="text-sm text-muted-foreground">Tidak ada promo aktif.</div>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Transaksi Terbaru</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Waktu</TableHead>
                <TableHead>Metode</TableHead>
                <TableHead>Inisiatif</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Struk</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="text-xs">{fmtDateTime(t.paid_at)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs uppercase">{t.payment_method}</Badge></TableCell>
                  <TableCell className="text-xs capitalize">{(t.initiative_type || "regular").replace("_", " ")}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmtIDR(t.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`print-tx-${t.id}`} onClick={() => printTxById(t.id)} title="Cetak struk">
                      <Printer className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-md" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Invoice Siap Dikirim</DialogTitle>
            <DialogDescription>
              {invoice?.invoice_no} • {invoice?.patient_name}
            </DialogDescription>
          </DialogHeader>
          {invoice && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-secondary/30 p-4 text-sm">
                {invoice.medical_record_no && <div className="flex justify-between"><span className="text-muted-foreground">No. RM</span><span data-testid="invoice-no-rm" className="font-medium">{invoice.medical_record_no}</span></div>}
                {invoice.treatment_items && invoice.treatment_items.length > 0 ? (
                  <div className="mb-1" data-testid="invoice-treatments">
                    <div className="text-muted-foreground mb-0.5">Treatment</div>
                    {invoice.treatment_items.map((t, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="pl-2">• {t.name}{Number(t.quantity) > 1 ? ` x${Number(t.quantity)}` : ""}</span><span>{fmtIDR(Number(t.unit_price || 0) * Number(t.quantity || 1))}</span></div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between"><span className="text-muted-foreground">Treatment</span><span>{invoice.treatment_name || "—"}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Dokter</span><span>{invoice.doctor_name || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Therapist</span><span data-testid="invoice-therapist">{invoice.therapist_name || "—"}</span></div>
                {invoice.cashier_name && <div className="flex justify-between"><span className="text-muted-foreground">Kasir</span><span data-testid="invoice-cashier">{invoice.cashier_name}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Metode</span><span className="uppercase">{invoice.payment_method}</span></div>
                {invoice.payments && invoice.payments.length > 1 && (
                  <div className="mt-1 space-y-0.5" data-testid="invoice-split">
                    {invoice.payments.map((p, i) => (
                      <div key={i} className="flex justify-between text-xs text-muted-foreground"><span className="uppercase pl-2">• {p.method}</span><span>{fmtIDR(p.amount)}</span></div>
                    ))}
                  </div>
                )}
                {invoice.promo_code && <div className="flex justify-between"><span className="text-muted-foreground">Promo</span><span>{invoice.promo_code}</span></div>}
                {invoice.discount_amount > 0 && <div className="flex justify-between text-emerald-700"><span>Diskon</span><span data-testid="invoice-discount">-{fmtIDR(invoice.discount_amount)}</span></div>}
                {invoice.deposit_used > 0 && <div className="flex justify-between text-indigo-700"><span>Potong Deposit</span><span data-testid="invoice-deposit-used">-{fmtIDR(invoice.deposit_used)}</span></div>}
                <div className="flex justify-between font-medium"><span className="text-muted-foreground">Sisa Saldo (Deposit+Cashback)</span><span data-testid="invoice-deposit-balance">{fmtIDR((Number(invoice.deposit_balance) || 0) + (Number(invoice.bonus_balance) || 0))}</span></div>
                {invoice.shipping_cost > 0 && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Ongkir{invoice.shipping?.courier ? ` (${invoice.shipping.courier})` : ""}</span><span data-testid="invoice-shipping">+{fmtIDR(invoice.shipping_cost)}</span></div>
                )}
                {invoice.shipping?.tracking_no && <div className="flex justify-between"><span className="text-muted-foreground">No. Resi</span><span>{invoice.shipping.tracking_no}</span></div>}
                <div className="mt-2 flex justify-between border-t border-border pt-2 font-medium">
                  <span>Total</span><span>{fmtIDR(invoice.amount)}</span>
                </div>
              </div>
              <div className="rounded-lg bg-card border border-border p-3 max-h-44 overflow-y-auto whitespace-pre-wrap text-xs text-muted-foreground" data-testid="invoice-preview">
                {invoice.wa_text}
              </div>
              <div className="text-xs text-muted-foreground">
                Customer akan menerima pesan WA berisi detail transaksi. Klik tombol di bawah untuk membuka WhatsApp Web/App dengan pesan siap kirim.
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" data-testid="copy-invoice-btn" onClick={copyInvoiceText}>
              Copy Teks
            </Button>
            <Button variant="outline" data-testid="print-receipt-btn" onClick={() => printReceipt(invoice)}>
              <Printer className="mr-1.5 h-4 w-4" /> Cetak Struk
            </Button>
            <Button data-testid="open-wa-btn" onClick={openWa} disabled={!invoice?.wa_url}>
              <MessageCircle className="mr-1.5 h-4 w-4" /> Buka WhatsApp Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
